'use client';

import classNames from 'classnames';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import { useAspectRatio } from '@/helpers/hooks';

import { ServiceHeroBlurb } from './ServiceHeroBlurb';
import { ServicePageTitle } from './ServicePageTitle';

const DEFAULT_IMAGE_TOP_OFFSET = 432;
const DESKTOP_IMAGE_WIDTH = 1078;
const IMAGE_BOTTOM_PADDING = 96;
const IMAGE_TOP_OFFSET_REDUCTION = 88;

type ObjectPosition =
  | 'object-bottom'
  | 'object-center'
  | 'object-left-bottom'
  | 'object-left-top'
  | 'object-left'
  | 'object-right-bottom'
  | 'object-right-top'
  | 'object-right'
  | 'object-top';

export interface ServicePageHeroProps {
  backgroundColor?: string;
  backgroundImage?: string;
  backgroundPosition?: ObjectPosition;
  heading: string;
  headingLargeText?: string;
  headingSmallText?: string;
  headingStatement?: string;
  headingStatementLines?: string[];
  imageTopOffset?: number;
  mobileBackgroundImage?: string;
  mobileBackgroundPosition?: ObjectPosition;
}

export function ServicePageHero({
  backgroundColor = 'bg-navy',
  backgroundImage,
  backgroundPosition = 'object-left-top',
  heading,
  headingLargeText,
  headingSmallText,
  headingStatement = '',
  headingStatementLines,
  imageTopOffset = DEFAULT_IMAGE_TOP_OFFSET,
  mobileBackgroundPosition = 'object-center',
}: ServicePageHeroProps) {
  const { isLandscape } = useAspectRatio();
  const [mounted, setMounted] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageHeight, setImageHeight] = useState<number | null>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (contentRef.current) {
      requestAnimationFrame(() => {
        if (contentRef.current) {
          const height = contentRef.current.offsetHeight;
          setContentHeight(height);
        }
      });
    }
  }, [heading, headingLargeText, headingSmallText, headingStatement, imageLoaded]);

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    setImageLoaded(true);
    if (isLandscape) {
      const img = e.currentTarget;
      const naturalWidth = img.naturalWidth;
      const naturalHeight = img.naturalHeight;
      if (naturalWidth > 0) {
        const renderedWidth = DESKTOP_IMAGE_WIDTH;
        const calculatedHeight = (naturalHeight / naturalWidth) * renderedWidth;
        setImageHeight(calculatedHeight);
      }
    }
  };

  const showBlurb = Boolean(headingLargeText || headingSmallText);

  const effectiveImageTopOffset = imageTopOffset - IMAGE_TOP_OFFSET_REDUCTION;

  const bottomPadding =
    imageHeight !== null && contentHeight !== null
      ? Math.max(0, effectiveImageTopOffset + imageHeight + IMAGE_BOTTOM_PADDING - contentHeight)
      : 0;

  return (
    <section
      className={classNames(
        'relative w-full overflow-x-hidden overflow-y-visible',
        !isLandscape && '-mt-[104px] min-h-[100dvh] pt-[104px]',
      )}
      style={{ paddingBottom: bottomPadding > 0 ? `${bottomPadding}px` : undefined }}
    >
      <div className={classNames('absolute inset-0', backgroundColor)} />

      {mounted && backgroundImage && (
        <>
          {isLandscape ? (
            <div
              className="absolute left-1/2 w-full max-w-[1200px] -translate-x-1/2"
              style={{ top: effectiveImageTopOffset }}
            >
              <Image
                src={backgroundImage}
                alt=""
                width={DESKTOP_IMAGE_WIDTH}
                height={0}
                className={`h-auto w-full max-w-[1078px] object-contain transition-opacity duration-500 ${backgroundPosition} ${
                  imageLoaded ? 'opacity-100' : 'opacity-0'
                }`}
                priority
                sizes="(max-width: 1200px) 100vw, 1078px"
                onLoad={handleImageLoad}
                unoptimized
              />
            </div>
          ) : (
            <Image
              src={backgroundImage}
              alt=""
              fill
              className={`object-cover transition-opacity duration-500 ${mobileBackgroundPosition} ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              priority
              sizes="100vw"
              onLoad={() => setImageLoaded(true)}
            />
          )}
        </>
      )}

      <div ref={contentRef}>
        <ServicePageTitle
          heading={heading}
          headingStatement={headingStatement}
          headingStatementLines={headingStatementLines}
        />
        {showBlurb && headingLargeText != null && headingSmallText != null && (
          <ServiceHeroBlurb headingLargeText={headingLargeText} headingSmallText={headingSmallText} />
        )}
      </div>
    </section>
  );
}
